import type { Json } from "@/integrations/supabase/types";

export const CONCEPT_CONTENT_KEYS = ["gift_cta_text", "gift_cta_link", "description_excerpt"] as const;
export const CONCEPT_AR_CONTENT_KEYS = ["gift_cta_text", "description_excerpt"] as const;

export function contentObject(value: Json | null | undefined): Record<string, Json | undefined> {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

export function conceptText(value: Json | null | undefined, key: string): string {
  const text = contentObject(value)[key];
  return typeof text === "string" ? text : "";
}

/** Prefer the editor's introduction; otherwise use complete opening sentences. */
export function conceptIntroduction(description: string, excerpt: string): string {
  if (excerpt.trim()) return excerpt;
  const opening = description.trim().split(/(?<=[.!?؟])\s+/u).slice(0, 2).join(" ");
  return opening || description;
}

/** New links have no guessed destination. Same-site absolute URLs use local routing. */
export function conceptGiftDestination(value: string): string | null {
  const link = value.trim();
  if (!link || /[\u0000-\u0020\u007f\\]/u.test(link)) return null;
  if (link.startsWith("/") && !link.startsWith("//")) return link;
  try {
    const url = new URL(link);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    if (["www.anayaperfume.com", "anayaperfume.com"].includes(url.hostname)) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.href;
  } catch {
    return null;
  }
}

type EditableSection = {
  section_title: string;
  section_title_ar: string | null;
  subtitle: string | null;
  subtitle_ar: string | null;
  description: string | null;
  description_ar: string | null;
  display_order: number | null;
  content: Json | null;
  content_ar: Json | null;
};
type ConceptForm = Partial<EditableSection> & {
  contentFields?: Record<string, string>;
  contentArFields?: Record<string, string>;
};

/** Only user-edited values are patched into a fresh row; conflicting edits fail closed. */
export function conceptSectionPatch(baseline: EditableSection, form: ConceptForm, latest: EditableSection) {
  const patch: Record<string, Json> = {};
  const columns = ["section_title", "section_title_ar", "subtitle", "subtitle_ar", "description", "description_ar", "display_order"] as const;
  for (const key of columns) {
    if (form[key] === undefined || (form[key] ?? "") === (baseline[key] ?? "")) continue;
    if (latest[key] !== baseline[key] && latest[key] !== form[key]) throw new Error("Section changed while editing. Reload before saving.");
    patch[key] = form[key] === "" ? null : form[key] ?? null;
  }
  const merge = (column: "content" | "content_ar", fields: Record<string, string> | undefined, keys: string[]) => {
    if (!fields) return;
    const before = contentObject(baseline[column]);
    const current = contentObject(latest[column]);
    const merged = { ...current };
    let changed = false;
    for (const key of keys) {
      const original = before[key] === undefined ? undefined : String(before[key] || "");
      const next = fields[key];
      // Empty new editor inputs are not seeded into the stored JSON.
      if (next === original || (original === undefined && !next)) continue;
      if (JSON.stringify(current[key]) !== JSON.stringify(before[key]) && current[key] !== next) throw new Error("Section changed while editing. Reload before saving.");
      if (next === undefined) delete merged[key];
      else merged[key] = next;
      changed = true;
    }
    if (changed) patch[column] = merged;
  };
  merge("content", form.contentFields, [...new Set([...Object.keys(contentObject(baseline.content)), ...Object.keys(form.contentFields || {})])]);
  merge("content_ar", form.contentArFields, [...CONCEPT_AR_CONTENT_KEYS]);
  return patch;
}